import * as React from "react";
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Stack,
    TextField,
    Typography,
    Alert,
    AlertColor,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { BiographySection } from "../../../services/types/bioApiTypes";
import { useSaveBiographySectionMutation } from "../../../services/cclTokenedEnterpriseCoachingBiographyApi";
import { AccessEventIndividualDetails } from "../../../services/types/accessEventTypes";
import { UserClaimsService } from "../../../services/currentUserService/currentUserService";
import useLogAccessEvent from "../../../hooks/useLogAccessEvent";
import CclAlertSnackbar from "../../common/cclAlertSnackbar";
import CclLoadingButton from "../../common/cclButtons/cclLoadingButton";
import CclButton from "../../common/cclButtons/cclButton";

interface BiographySectionAccordianProps {
    expanded: boolean;
    editMode: boolean;
    bioSection: BiographySection;
    bioId: number;
    key: number;
    pKey: string;
    readonly?: boolean
    imKey?: string;
    email?: string;
    fromResources: boolean;
}

const BiographySectionAccordian: React.FC<BiographySectionAccordianProps> = (props) => {
    const claimservice = new UserClaimsService();
    const [showSnackbar, setShowSnackbar] = React.useState<boolean>(false);
    const [snackbarMessage, setSnackbarMessage] = React.useState<string>("");
    const [snackbarSeverity, setSnackbarSeverity] = React.useState<AlertColor | undefined>("info");

    const [contentValue, setContentValue] = React.useState<string | undefined>(
        props.bioSection.sectionContent
    );
    const [contentEdited, setContentEdited] = React.useState<boolean>(false);
    const [expanded, setExpanded] = React.useState<boolean>(props.expanded);
    const [saveBiographySection, { isLoading }] = useSaveBiographySectionMutation();
    const { logEvent } = useLogAccessEvent();

    const savedBioEvent = (imkey: string) => {
        const tag: string = !props.fromResources ? "UserSavedBio" : "BioEdited";
        const evtData: AccessEventIndividualDetails = {
            imKey: imkey,
            email: props.email ?? claimservice.GetCurrentUserEmail(),
        };

        logEvent(tag, evtData);
    };

    const ShowSnackbarElement = (message: string, severity: AlertColor) => {
        setShowSnackbar(true);
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
    };

    const saveBiographySectionClick = () => {
        let section: BiographySection = {
            id: props.bioSection.id,
            bioId: props.bioId,
            title: props.bioSection.title,
            sectionContent: contentValue ? contentValue : "",
            pKey: Number(props.pKey),
        };
        setContentEdited(false);
        saveBiographySection(section)
            .unwrap()
            .then(() => {
                ShowSnackbarElement("Bio Updated", "success");
                savedBioEvent(props.imKey ? props.imKey : claimservice.GetCurrentUserId());
            })
            .catch((error) => {
                ShowSnackbarElement("Bio Update Failed", "error");
            });
    };

    const handleAccordianChange = () => (event: React.SyntheticEvent, newExpanded: boolean) => {
        setExpanded(newExpanded ? true : false);
    };

    const handleContentChange = (newText: string) => {
        setContentValue(newText);
        setContentEdited(newText !== props.bioSection.sectionContent);
    };

    const clearContent = () => {
        setContentValue("");
        setContentEdited(true);
    }

    const ariaControlId = "panel-content-" + props.bioSection.id;
    const accordianId = "panel-header-" + props.bioSection.id;

    let contentLength = 900;
    
    if (props.bioSection.title === "Experience" || props.bioSection.title === "Current Role") {
        contentLength = 1350;
    }
    if (props.bioSection.title === "Educational Background" || props.bioSection.title === "Professional Affiliations") {
        contentLength = 450;
    }

    return (
        <Accordion key={+expanded} expanded={expanded} onChange={handleAccordianChange()}>
            {showSnackbar ? (
                <CclAlertSnackbar
                    open={true}
                    onClose={() => setShowSnackbar(false)}
                    message={snackbarMessage}
                    severity={snackbarSeverity}
                />
            ) : null}
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls={ariaControlId}
                id={accordianId}
            >
                <Typography sx={{ marginLeft: "15px", fontWeight: "bolder" }}>
                    {props.bioSection.title}
                </Typography>
            </AccordionSummary>
            <AccordionDetails>
                {!props.editMode && (
                    <span
                        dangerouslySetInnerHTML={{
                            __html:
                                props.bioSection.sectionContent &&
                                props.bioSection.sectionContent.length > 0
                                    ? props.bioSection.sectionContent.replace(/\n/g, "<br />")
                                    : "This section has no content",
                        }}
                    />
                )}
                {props.editMode && (
                    <Stack width={1} spacing={3}>
                        {props.readonly ? (
                            <Typography
                                sx={{
                                    border: "1px solid lightGray",
                                    padding: 2,
                                    borderRadius: "6px",
                                    minHeight: "100px",
                                    whiteSpace: "pre-wrap",
                                }}
                            >
                                {contentValue}
                            </Typography>
                        )
                        :
                        (
                            <TextField
                                inputProps={{ maxLength: contentLength }}
                                onChange={(e) => handleContentChange(e.target.value)}
                                label={`Enter ${props.bioSection.title}`}
                                value={contentValue}
                                disabled={props.readonly}
                                multiline={true}
                                rows={8}
                                fullWidth={true}
                            />
                        )}

                        {!props.readonly && (
                            <>
                                <Typography variant="caption" display="block" gutterBottom>
                                    {contentValue
                                        ? (contentLength - contentValue.length).toString() +
                                          ` of ${contentLength} characters remaining`
                                        : `${contentLength} characters remaining`}
                                </Typography>
                                <Stack direction={"row"} alignItems={"center"}>
                                    {contentEdited && (
                                        <Alert sx={{ width: "100%", mr: 2 }} severity="info">
                                            You have pending changes. Press Save to commit them.
                                        </Alert>
                                    )}
                                    <CclButton
                                        onClick={clearContent}
                                        variant="outlined"
                                        disabled={contentValue?.length === 0 || contentValue === undefined || contentValue === null || contentValue === "" || props.readonly}
                                        size="small"
                                        sx={{ mr: 2, height: "36px", ml: "auto", }} mode={"secondary"}                            
                                    >
                                        Clear
                                    </CclButton>
                                    <CclLoadingButton
                                        onClick={saveBiographySectionClick}
                                        loading={isLoading}
                                        disabled={!contentEdited}
                                        sx={{ mr: 2, ml: "2", height: "36px" }}
                                        mode={"primary"}
                                        size="small"
                                    >
                                        Save
                                    </CclLoadingButton>
                                </Stack>
                            </>
                        )}
                    </Stack>
                )}
            </AccordionDetails>
        </Accordion>
    );
};

export default BiographySectionAccordian;
